import { Box, CircularProgress, Typography } from '@mui/material';
import { CheckCircleOutlined, ErrorOutlined, RadioButtonUnchecked } from '@mui/icons-material';
import { Colors } from '../../../../ui/theme/colors';
import { StepView } from '../../logic/processing-steps';

export interface ProcessingStepProps {
  steps: StepView[];
  /** True once a step has succeeded: what failed is now a repair, not a fresh attempt. */
  isRepairing: boolean;
}

/**
 * The calls are not atomic with each other, so what succeeded is shown rather than implied. A retry
 * resumes at the first row that is not yet ticked.
 */
export function ProcessingStep({ steps, isRepairing }: ProcessingStepProps) {
  const failed = steps.find((step) => step.status === 'failed');

  return (
    <Box
      style={{ gap: 12 }}
      sx={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {steps.map((step) => (
        <Box
          key={step.id}
          style={{ gap: 12 }}
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <StepIcon status={step.status} />
          <Typography variant="body2" color={step.status === 'pending' ? 'textSecondary' : 'textPrimary'}>
            {step.label}
          </Typography>
        </Box>
      ))}

      {failed?.error && (
        <Box
          sx={{
            marginTop: 1
          }}
        >
          <Typography variant="body2" color="error">
            {failed.error}
          </Typography>
          {isRepairing && (
            <Typography variant="caption" color="textSecondary">
              The steps already ticked are done and will not be repeated. Try again to finish the rest.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function StepIcon({ status }: { status: StepView['status'] }) {
  const size = { width: 20, height: 20 };

  switch (status) {
    case 'done':
      return <CheckCircleOutlined style={{ ...size, color: Colors.collectedGreen }} />;
    case 'failed':
      return <ErrorOutlined style={{ ...size, color: Colors.cancelledDeletedRed }} />;
    case 'running':
      return <CircularProgress size={18} />;
    default:
      return <RadioButtonUnchecked style={{ ...size, color: Colors.midGray }} />;
  }
}
